import { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { showToast } from '../../../components/Toasts'

const RESET_DELAY_MS = 2000

const fallbackCopyToClipboard = (value: string) => {
  if (typeof document === 'undefined') {
    throw new Error('Clipboard API is not available')
  }

  const input = document.createElement('input')
  input.value = value
  document.body.appendChild(input)
  input.select()
  document.execCommand('copy')
  document.body.removeChild(input)
}

export const useCopy = (text: string, alertText?: string | null) => {
  const { t } = useTranslation()
  const [copyLabel, setCopyLabel] = useState(t('copy'))
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const scheduleReset = useCallback(() => {
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current)
    }

    resetTimeoutRef.current = setTimeout(() => {
      setCopyLabel(t('copy'))
    }, RESET_DELAY_MS)
  }, [])

  const handleCopy = useCallback(async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        fallbackCopyToClipboard(text)
      }

      setCopyLabel(t('copied'))
      scheduleReset()

      showToast?.info(alertText || t('copied'))
    } catch (error) {}
  }, [text, scheduleReset])

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current)
      }
    }
  }, [])

  return {
    copyLabel,
    handleCopy,
  }
}

export default useCopy
